import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Modal, Button, Typography, Alert } from "antd";
import { ExportOutlined, ClockCircleOutlined } from "@ant-design/icons";
// import { getSchedulerUrlForProvider } from "../../../../services/zoomSchedulerLinks"; // old static-table lookup, superseded below
import { withPatientReferenceParams } from "../../../../services/zoomSchedulerLinks";
import { isCareGuideCategory } from "../../../../utils/providerCategories";
import neutralDoctorAvatar from "../../../../assets/images/users/clinician_default_avatar.png";

const { Text, Title } = Typography;

const AVATAR_COLORS = ["#7C3AED", "#2563EB", "#EA580C", "#15803D", "#DB2777"];

function initialsOf(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

function colorForName(name) {
  const str = String(name || "");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function categoryLabel(group) {
  if (group?.__careGuidePlaceholder) return "Care Guide";
  return group?.groupName || "Appointment";
}

// Initial sessions run an hour; follow ups are half that. These are two
// separate scheduler links per provider, not one link with a duration picker.
const DURATION_LABEL = {
  initial: "1 hour",
  followUp: "30 minutes",
  careGuide: "15 minutes",
};

export default function BookingModal({
  open,
  clinician,
  group,
  categoryId,
  isCareGuide: isCareGuideProp,
  noCredits,
  isFollowUp,
  patient,
  onCancel,
}) {
  const [openedScheduler, setOpenedScheduler] = useState(false);

  // const schedulerUrl = getSchedulerUrlForProvider(clinician, categoryId, isFollowUp, patient); // old static-table lookup
  const initialUrl = withPatientReferenceParams(clinician?.initialBookingLink, patient);
  const followUpUrl = withPatientReferenceParams(clinician?.followUpBookingLink, patient);
  // Care Guide: free 15-minute call - no intake forms, payment or credits needed.
  const isCareGuide = !!isCareGuideProp || isCareGuideCategory(categoryId) || !!group?.__careGuidePlaceholder;

  // The patient picks; isFollowUp (from their appointment history) only decides which button is highlighted.
  const bookingOptions = (
    isCareGuide
      ? [{ key: "careGuide", label: openedScheduler ? "Reopen scheduler" : "Schedule Appointment", url: initialUrl }]
      : [
          { key: "initial", label: `Initial · ${DURATION_LABEL.initial}`, url: initialUrl },
          { key: "followUp", label: `Follow-up · ${DURATION_LABEL.followUp}`, url: followUpUrl },
        ]
  ).filter((o) => !!o.url);
  const suggestedKey = isCareGuide ? "careGuide" : isFollowUp ? "followUp" : "initial";

  const handleOpenScheduler = (url) => {
    if (!url || noCredits) return;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpenedScheduler(true);
  };

  const handleCancel = () => {
    setOpenedScheduler(false);
    onCancel?.();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={560}
      destroyOnClose
      title={`Book with ${clinician?.name || "clinician"}`}
    >
      {!clinician ? null : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            {clinician.picture ? (
              <img
                src={clinician.picture}
                alt=""
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = neutralDoctorAvatar;
                }}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  objectFit: "cover",
                  background: "#e2e8f0",
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: colorForName(clinician.name),
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {initialsOf(clinician.name)}
              </div>
            )}
            <Text strong style={{ fontSize: 15 }}>
              {clinician.name}
            </Text>
          </div>

          <Title level={4} style={{ margin: "0 0 8px" }}>
            {categoryLabel(group)}
          </Title>

          {isCareGuide && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 20,
                fontSize: 14,
                color: "#334155",
              }}
            >
              <ClockCircleOutlined style={{ color: "#64748b" }} />
              <span>{DURATION_LABEL.careGuide}</span>
            </div>
          )}

          {bookingOptions.length > 0 ? (
            <>
              <Text style={{ display: "block", color: "#595959", marginBottom: 20 }}>
                Pick a time on {clinician.name}&apos;s scheduling page. Their live availability
                lives there, and you&apos;ll get a confirmation email with the meeting details
                once you&apos;re booked.
              </Text>

              {noCredits && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 20 }}
                  message="No credits left for this provider type"
                  description={
                    <>
                      You need an available credit to book with {clinician.name}.{" "}
                      <Link to="/plans">View care plans</Link>
                    </>
                  }
                />
              )}

              {openedScheduler && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 20 }}
                  message="Finish booking in the new tab"
                  description="Your appointment will appear under Appointments once it's confirmed."
                />
              )}

              {!isCareGuide && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 20 }}
                  message="Complete at least 72 hours before your appointment:"
                  description={
                    <>
                      <ul style={{ margin: "4px 0 8px", paddingLeft: 18 }}>
                        <li>Complete any required intake forms</li>
                        <li>Ensure you have the required plan or appointment credits</li>
                      </ul>
                      If these steps aren&apos;t completed at least 72 hours before your appointment, it
                      may be automatically cancelled so the time can be offered to another patient.
                    </>
                  }
                />
              )}

              <div style={{ display: "flex", gap: 12 }}>
                {bookingOptions.map((o) => {
                  const highlighted = o.key === suggestedKey || bookingOptions.length === 1;
                  return (
                    <Button
                      key={o.key}
                      type={highlighted ? "primary" : "default"}
                      size="large"
                      icon={<ExportOutlined />}
                      style={{
                        flex: 1,
                        ...(highlighted
                          ? { background: "#00ADEF", borderColor: "#00ADEF" }
                          : { color: "#00ADEF", borderColor: "#00ADEF" }),
                      }}
                      onClick={() => handleOpenScheduler(o.url)}
                      disabled={noCredits}
                    >
                      {o.label}
                    </Button>
                  );
                })}
              </div>
              <div style={{ textAlign: "center", marginTop: 12 }}>
                <Button type="link" onClick={handleCancel} style={{ color: "#64748b" }}>
                  {openedScheduler ? "Done" : "Cancel"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Text style={{ display: "block", color: "#595959", marginBottom: 20 }}>
                {clinician.name} doesn&apos;t have a scheduling link set up yet. Please contact
                your care team and they&apos;ll get you booked.
              </Text>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <Button onClick={handleCancel}>Close</Button>
                <Button type="primary" icon={<ExportOutlined />} disabled>
                  Schedule Appointment
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
